/**
 * Onboarding-mail generator — pure logic, no React.
 *
 * Produces three branded customer-onboarding emails (announce / how / go-live)
 * in either Dutch or English, with formal / casual register toggle. The HTML
 * is fully self-contained (inline styles, no external CSS) so it can be pasted
 * into Outlook, Gmail, Brevo, Mailchimp, etc.
 */
import type { Lang } from './i18n';

export type Tone = 'formal' | 'casual';
export type TabId = 'announce' | 'how' | 'golive';

export interface MailState {
  company: string;
  website: string;
  email: string;
  phone: string;
  logo: string;           // data URL
  logoName: string;
  primary: string;        // hex
  font: string;           // CSS font-family stack
  tone: Tone;
  service: string;
  golive: string;
  showPrice: boolean;
  price: string;          // string so we can preserve the user's exact input
  priceUnit: string;      // matches one of PRICE_UNITS[lang]
}

export interface BuiltMail {
  subject: string;
  html: string;
}

export interface BuiltMails {
  announce: BuiltMail;
  how: BuiltMail;
  golive: BuiltMail;
}

export const DEFAULT_STATE: MailState = {
  company: '', website: '', email: '', phone: '', logo: '', logoName: '',
  primary: '#1C5FD7',
  font: 'Arial, Helvetica, sans-serif',
  tone: 'formal',
  service: '', golive: '',
  showPrice: false,
  price: '',
  priceUnit: '',
};

export const PALETTE = ['#1C5FD7', '#0F172A', '#16A34A', '#DC2626', '#6B5BF5', '#0EA5C0', '#EA580C', '#9A7416'];

export const FONT_CHOICES: Array<{ value: string; labelNl: string; labelEn: string }> = [
  { value: 'Arial, Helvetica, sans-serif',           labelNl: 'Modern sans (Arial)',           labelEn: 'Modern sans (Arial)' },
  { value: "'Trebuchet MS', Verdana, sans-serif",    labelNl: 'Vriendelijk sans (Trebuchet)',  labelEn: 'Friendly sans (Trebuchet)' },
  { value: "Georgia, 'Times New Roman', serif",       labelNl: 'Klassiek serif (Georgia)',      labelEn: 'Classic serif (Georgia)' },
  { value: 'Tahoma, Geneva, sans-serif',             labelNl: 'Compact sans (Tahoma)',         labelEn: 'Compact sans (Tahoma)' },
];

export const PRICE_UNITS: Record<Lang, string[]> = {
  nl: ['per verificatie', 'per controle', 'eenmalig'],
  en: ['per verification', 'per check', 'one-time'],
};

// Default suggested unit price shown in the placeholder. Updated 2026-06 from
// 7,50 → 9,00 to reflect the current recommended sell price.
export const DEFAULT_PRICE_PLACEHOLDER: Record<Lang, string> = {
  nl: '9,00',
  en: '9.00',
};

// ── helpers ─────────────────────────────────────────────────────────────────
const esc = (s: unknown): string => String(s ?? '')
  .replace(/&/g, '&amp;')
  .replace(/</g, '&lt;')
  .replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;');

const isHex = (h: string): boolean => /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.test(h);

function readableOn(hex: string): string {
  const c = hex.replace('#', '').padStart(6, '0');
  const full = c.length === 3 ? c[0] + c[0] + c[1] + c[1] + c[2] + c[2] : c;
  const r = parseInt(full.substr(0, 2), 16);
  const g = parseInt(full.substr(2, 2), 16);
  const b = parseInt(full.substr(4, 2), 16);
  return ((0.299 * r + 0.587 * g + 0.114 * b) / 255) > 0.62 ? '#0E1626' : '#FFFFFF';
}

function shade(hex: string, pct: number): string {
  const c = hex.replace('#', '');
  const full = c.length === 3 ? c[0] + c[0] + c[1] + c[1] + c[2] + c[2] : c;
  let r = parseInt(full.substr(0, 2), 16);
  let g = parseInt(full.substr(2, 2), 16);
  let b = parseInt(full.substr(4, 2), 16);
  r = Math.max(0, Math.min(255, Math.round(r + (pct / 100) * 255)));
  g = Math.max(0, Math.min(255, Math.round(g + (pct / 100) * 255)));
  b = Math.max(0, Math.min(255, Math.round(b + (pct / 100) * 255)));
  return '#' + [r, g, b].map(x => ('0' + x.toString(16)).slice(-2)).join('');
}

// Resolves an empty user field to the language-specific fallback.
interface Resolved {
  company: string; service: string; golive: string;
  email: string; website: string; phone: string;
  primary: string; priceLine: string;
}

function resolve(state: MailState, lang: Lang): Resolved {
  const primary = isHex(state.primary) ? state.primary : '#1C5FD7';
  const fb = lang === 'nl' ? {
    company: 'Uw Bedrijf',
    service: 'beveiligde identiteitscontrole',
    golive: 'binnenkort',
    email: 'support@uwbedrijf.be',
  } : {
    company: 'Your Company',
    service: 'secure identity verification',
    golive: 'soon',
    email: 'support@yourcompany.com',
  };
  let priceLine = '';
  if (state.showPrice && state.price.trim()) {
    const formatted = lang === 'nl'
      ? String(state.price).replace('.', ',')
      : String(state.price).replace(',', '.');
    const unit = state.priceUnit || PRICE_UNITS[lang][0];
    priceLine = `€ ${formatted} ${unit}`;
  }
  return {
    company:  state.company.trim() || fb.company,
    service:  state.service.trim() || fb.service,
    golive:   state.golive.trim()  || fb.golive,
    email:    state.email.trim()   || fb.email,
    website:  state.website.trim(),
    phone:    state.phone.trim(),
    primary,
    priceLine,
  };
}

// ── shared email shell (header / footer / safe table layout) ────────────────
function shell(state: MailState, d: Resolved, inner: string, preheader: string, lang: Lang): string {
  const on = readableOn(d.primary);
  const logo = state.logo
    ? `<img src="${state.logo}" alt="${esc(d.company)}" style="max-height:46px;max-width:180px;display:block;border:0;">`
    : `<span style="font-family:${state.font};font-size:20px;font-weight:bold;color:${on};">${esc(d.company)}</span>`;

  const footerLinks: string[] = [];
  if (d.website) {
    footerLinks.push(`<a href="${esc(d.website)}" style="color:${d.primary};text-decoration:none;">${esc(d.website.replace(/^https?:\/\//, ''))}</a>`);
  }
  footerLinks.push(`<a href="mailto:${esc(d.email)}" style="color:${d.primary};text-decoration:none;">${esc(d.email)}</a>`);
  if (d.phone) footerLinks.push(esc(d.phone));

  const footerNote = lang === 'nl'
    ? (state.tone === 'formal' ? 'U ontvangt deze e-mail omdat u klant bent bij ' : 'Je krijgt deze e-mail omdat je klant bent bij ')
    : (state.tone === 'formal' ? 'You are receiving this email because you are a customer of '   : "You're receiving this email because you're a customer of ");

  return `<!DOCTYPE html><html lang="${lang}"><head><meta charset="utf-8"><meta name="viewport" content="width=device-width,initial-scale=1"><meta name="x-apple-disable-message-reformatting"><title>${esc(d.company)}</title></head>` +
    `<body style="margin:0;padding:0;background:#EDF0F5;font-family:${state.font};">` +
    `<div style="display:none;max-height:0;overflow:hidden;opacity:0;">${esc(preheader)}</div>` +
    `<table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="background:#EDF0F5;padding:24px 12px;"><tr><td align="center">` +
    `<table role="presentation" width="600" cellpadding="0" cellspacing="0" style="max-width:600px;width:100%;background:#fff;border-radius:12px;overflow:hidden;box-shadow:0 8px 28px rgba(14,22,38,.10);">` +
    `<tr><td style="background:${d.primary};padding:24px 32px;">${logo}</td></tr>` +
    `<tr><td style="padding:36px 32px 12px;">${inner}</td></tr>` +
    `<tr><td style="padding:24px 32px 32px;border-top:1px solid #EDF0F5;">` +
    `<p style="margin:0 0 6px;font-family:${state.font};font-size:12px;line-height:1.6;color:#8A93A6;">${esc(d.company)}</p>` +
    `<p style="margin:0;font-family:${state.font};font-size:12px;line-height:1.6;color:#8A93A6;">${footerLinks.join(' &nbsp;·&nbsp; ')}</p>` +
    `</td></tr></table>` +
    `<p style="font-family:${state.font};font-size:11px;color:#A9B0BE;margin:16px 0 0;">${footerNote}${esc(d.company)}.</p>` +
    `</td></tr></table></body></html>`;
}

const h1 = (state: MailState, t: string) =>
  `<h1 style="margin:0 0 16px;font-family:${state.font};font-size:24px;line-height:1.25;color:#0E1626;font-weight:bold;">${t}</h1>`;

const p = (state: MailState, t: string) =>
  `<p style="margin:0 0 16px;font-family:${state.font};font-size:15px;line-height:1.65;color:#3A4358;">${t}</p>`;

function btn(state: MailState, d: Resolved, label: string): string {
  const on = readableOn(d.primary);
  return `<table role="presentation" cellpadding="0" cellspacing="0" style="margin:8px 0 20px;"><tr><td style="border-radius:8px;background:${d.primary};"><a href="${d.website || '#'}" style="display:inline-block;padding:13px 26px;font-family:${state.font};font-size:14px;font-weight:bold;color:${on};text-decoration:none;border-radius:8px;">${label}</a></td></tr></table>`;
}

// ── NL email bodies (preserve U-vorm / Je-vorm) ─────────────────────────────
function buildNlAnnounce(state: MailState): BuiltMail {
  const d = resolve(state, 'nl');
  const T = (u: string, je: string) => state.tone === 'casual' ? je : u;
  const subject = T(
    `Een extra beveiliging voor uw account bij ${d.company}`,
    `Een extra beveiliging voor je account bij ${d.company}`,
  );
  const priceLine = d.priceLine ? p(state, 'Deze service is inbegrepen.') : '';
  const inner =
    h1(state, T('We beschermen uw account nog beter', 'We beschermen je account nog beter')) +
    p(state, T('Beste klant,', 'Hoi,')) +
    p(state, T(
      `Uw veiligheid is voor ons een prioriteit. Daarom voeren we binnenkort een <strong>${esc(d.service)}</strong> in. Zo zijn we zeker dat enkel u toegang krijgt tot uw gegevens — en niemand anders die zich voor u zou willen uitgeven.`,
      `Jouw veiligheid is voor ons een prioriteit. Daarom voeren we binnenkort een <strong>${esc(d.service)}</strong> in. Zo zijn we zeker dat enkel jij toegang krijgt tot je gegevens — en niemand anders die zich voor jou zou willen uitgeven.`,
    )) +
    p(state, T(
      'Concreet: bij bepaalde gevoelige aanvragen — zoals een wachtwoordreset of een wijziging van uw gegevens — vragen we u kort om uw identiteit te bevestigen. Dat duurt minder dan een minuut.',
      'Concreet: bij bepaalde gevoelige aanvragen — zoals een wachtwoordreset of een wijziging van je gegevens — vragen we je kort om je identiteit te bevestigen. Dat duurt minder dan een minuut.',
    )) +
    priceLine +
    p(state, T(
      'U hoeft nu nog niets te doen. In een volgende e-mail leggen we uit hoe het werkt.',
      'Je hoeft nu nog niets te doen. In een volgende e-mail leggen we uit hoe het werkt.',
    )) +
    btn(state, d, T('Meer over uw veiligheid', 'Meer over je veiligheid')) +
    p(state, T('Met vriendelijke groet,<br>Het team van ', 'Groetjes,<br>Het team van ') + esc(d.company));
  return { subject, html: shell(state, d, inner, T('We voeren een extra beveiliging in voor uw account.', 'We voeren een extra beveiliging in voor je account.'), 'nl') };
}

function buildNlHow(state: MailState): BuiltMail {
  const d = resolve(state, 'nl');
  const on = readableOn(d.primary);
  const soft = shade(d.primary, 42);
  const T = (u: string, je: string) => state.tone === 'casual' ? je : u;
  const subject = 'Zo werkt onze nieuwe identiteitscontrole — in 30 seconden';
  const step = (n: string, t: string, x: string) =>
    `<table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="margin:0 0 14px;"><tr><td width="46" valign="top"><div style="width:34px;height:34px;border-radius:50%;background:${d.primary};color:${on};font-family:${state.font};font-size:15px;font-weight:bold;text-align:center;line-height:34px;">${n}</div></td><td valign="top" style="padding-left:6px;"><p style="margin:0 0 2px;font-family:${state.font};font-size:15px;font-weight:bold;color:#0E1626;">${t}</p><p style="margin:0;font-family:${state.font};font-size:14px;line-height:1.55;color:#3A4358;">${x}</p></td></tr></table>`;
  const inner =
    h1(state, 'Zo werkt het — in 30 seconden') +
    p(state, T(
      'Binnenkort vragen we u bij gevoelige aanvragen om uw identiteit te bevestigen. Geen zorgen: het is eenvoudig en snel. Dit kan u verwachten:',
      'Binnenkort vragen we je bij gevoelige aanvragen om je identiteit te bevestigen. Geen zorgen: het is eenvoudig en snel. Dit kan je verwachten:',
    )) +
    `<div style="background:${soft};border-radius:10px;padding:22px 22px 8px;margin:4px 0 18px;">` +
      step('1', T('U krijgt een sms', 'Je krijgt een sms'), T('Met een veilige link. U hoeft niets te installeren.', 'Met een veilige link. Je hoeft niets te installeren.')) +
      step('2', T('U bevestigt uw identiteit', 'Je bevestigt je identiteit'), T('Een korte controle via uw smartphone — meestal met uw ID en een selfie.', 'Een korte controle via je smartphone — meestal met je ID en een selfie.')) +
      step('3', 'Klaar', T('Wij verwerken uw aanvraag meteen verder. Veilig en wel.', 'We verwerken je aanvraag meteen verder. Veilig en wel.')) +
    `</div>` +
    p(state, T(
      'Uw gegevens worden vertrouwelijk en conform de privacywetgeving (AVG) verwerkt. We bewaren geen kopie van uw identiteitsdocument.',
      'Je gegevens worden vertrouwelijk en conform de privacywetgeving (AVG) verwerkt. We bewaren geen kopie van je identiteitsdocument.',
    )) +
    p(state, T('Vragen? Antwoord gerust op deze e-mail, we helpen u graag verder.', 'Vragen? Antwoord gerust op deze e-mail, we helpen je graag verder.')) +
    p(state, T('Met vriendelijke groet,<br>Het team van ', 'Groetjes,<br>Het team van ') + esc(d.company));
  return { subject, html: shell(state, d, inner, T('In 3 eenvoudige stappen bevestigt u uw identiteit.', 'In 3 eenvoudige stappen bevestig je je identiteit.'), 'nl') };
}

function buildNlGolive(state: MailState): BuiltMail {
  const d = resolve(state, 'nl');
  const soft = shade(d.primary, 42);
  const T = (u: string, je: string) => state.tone === 'casual' ? je : u;
  const subject = `Vanaf ${d.golive}: onze identiteitscontrole gaat live`;
  const inner =
    h1(state, 'Het is bijna zover') +
    p(state, T(
      `Vanaf <strong>${esc(d.golive)}</strong> is onze ${esc(d.service)} actief. Vanaf dan vragen we u bij gevoelige aanvragen kort om uw identiteit te bevestigen — precies zoals we eerder uitlegden.`,
      `Vanaf <strong>${esc(d.golive)}</strong> is onze ${esc(d.service)} actief. Vanaf dan vragen we je bij gevoelige aanvragen kort om je identiteit te bevestigen — precies zoals we eerder uitlegden.`,
    )) +
    `<div style="background:${soft};border-radius:10px;padding:18px 22px;margin:4px 0 18px;">` +
      `<p style="margin:0 0 6px;font-family:${state.font};font-size:14px;font-weight:bold;color:#0E1626;">${T('Wat moet u doen?', 'Wat moet je doen?')}</p>` +
      `<p style="margin:0;font-family:${state.font};font-size:14px;line-height:1.6;color:#3A4358;">${T('Niets op voorhand. Wanneer het nodig is, krijgt u vanzelf een sms met instructies. Hou uw smartphone bij de hand.', 'Niets op voorhand. Wanneer het nodig is, krijg je vanzelf een sms met instructies. Hou je smartphone bij de hand.')}</p>` +
    `</div>` +
    p(state, `<strong>${T('Vragen of hulp nodig?', 'Vragen of hulp nodig?')}</strong><br>${T('Ons team staat voor u klaar. ', 'Ons team staat voor je klaar. ')}${d.phone ? `Bel ons op ${esc(d.phone)} of mail ` : 'Mail '}<a href="mailto:${esc(d.email)}" style="color:${d.primary};">${esc(d.email)}</a>.`) +
    btn(state, d, 'Contacteer ons') +
    p(state, T('Bedankt voor uw vertrouwen,<br>Het team van ', 'Bedankt voor je vertrouwen,<br>Het team van ') + esc(d.company));
  return { subject, html: shell(state, d, inner, `Onze identiteitscontrole gaat live op ${d.golive}.`, 'nl') };
}

// ── EN email bodies (formal vs casual register) ─────────────────────────────
function buildEnAnnounce(state: MailState): BuiltMail {
  const d = resolve(state, 'en');
  const T = (formal: string, casual: string) => state.tone === 'casual' ? casual : formal;
  const subject = `An extra layer of security for your ${d.company} account`;
  const priceLine = d.priceLine ? p(state, 'This service is included.') : '';
  const inner =
    h1(state, 'Strengthening the security of your account') +
    p(state, T('Dear customer,', 'Hi,')) +
    p(state, `Your security is a priority for us. That's why we are soon introducing a <strong>${esc(d.service)}</strong>. This way we make sure that only you can access your data — and not someone trying to impersonate you.`) +
    p(state, T(
      'Concretely: for certain sensitive requests — such as a password reset or a change to your details — we will briefly ask you to confirm your identity. It takes less than a minute.',
      'Concretely: for certain sensitive requests — like a password reset or a change to your details — we\'ll briefly ask you to confirm your identity. It takes less than a minute.',
    )) +
    priceLine +
    p(state, T(
      'You do not need to do anything yet. In a follow-up email we will explain how it works.',
      "You don't need to do anything yet. In a follow-up email we'll explain how it works.",
    )) +
    btn(state, d, 'More about your security') +
    p(state, T('Kind regards,<br>The team at ', 'Cheers,<br>The team at ') + esc(d.company));
  return { subject, html: shell(state, d, inner, 'We are introducing an extra layer of security for your account.', 'en') };
}

function buildEnHow(state: MailState): BuiltMail {
  const d = resolve(state, 'en');
  const on = readableOn(d.primary);
  const soft = shade(d.primary, 42);
  const T = (formal: string, casual: string) => state.tone === 'casual' ? casual : formal;
  const subject = 'How our new identity check works — in 30 seconds';
  const step = (n: string, t: string, x: string) =>
    `<table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="margin:0 0 14px;"><tr><td width="46" valign="top"><div style="width:34px;height:34px;border-radius:50%;background:${d.primary};color:${on};font-family:${state.font};font-size:15px;font-weight:bold;text-align:center;line-height:34px;">${n}</div></td><td valign="top" style="padding-left:6px;"><p style="margin:0 0 2px;font-family:${state.font};font-size:15px;font-weight:bold;color:#0E1626;">${t}</p><p style="margin:0;font-family:${state.font};font-size:14px;line-height:1.55;color:#3A4358;">${x}</p></td></tr></table>`;
  const inner =
    h1(state, 'How it works — in 30 seconds') +
    p(state, T(
      'Soon, for sensitive requests, we will ask you to confirm your identity. No worries: it is simple and quick. Here is what to expect:',
      "Soon, for sensitive requests, we'll ask you to confirm your identity. No worries: it's simple and quick. Here's what to expect:",
    )) +
    `<div style="background:${soft};border-radius:10px;padding:22px 22px 8px;margin:4px 0 18px;">` +
      step('1', 'You receive an SMS', T('With a secure link. You do not need to install anything.', "With a secure link. You don't need to install anything.")) +
      step('2', 'You confirm your identity', 'A quick check on your smartphone — usually with your ID and a selfie.') +
      step('3', 'Done', T('We continue processing your request immediately. Safe and sound.', "We'll continue processing your request right away. Safe and sound.")) +
    `</div>` +
    p(state, 'Your data is processed confidentially and in line with privacy law (GDPR). We do not retain a copy of your identity document.') +
    p(state, T('Questions? Feel free to reply to this email — we are happy to help.', "Questions? Feel free to reply to this email — we're happy to help.")) +
    p(state, T('Kind regards,<br>The team at ', 'Cheers,<br>The team at ') + esc(d.company));
  return { subject, html: shell(state, d, inner, 'Confirm your identity in 3 simple steps.', 'en') };
}

function buildEnGolive(state: MailState): BuiltMail {
  const d = resolve(state, 'en');
  const soft = shade(d.primary, 42);
  const T = (formal: string, casual: string) => state.tone === 'casual' ? casual : formal;
  const subject = `Starting ${d.golive}: our identity check goes live`;
  const inner =
    h1(state, 'It is almost time') +
    p(state, T(
      `From <strong>${esc(d.golive)}</strong>, our ${esc(d.service)} is active. From then on, for sensitive requests, we will briefly ask you to confirm your identity — exactly as we explained earlier.`,
      `From <strong>${esc(d.golive)}</strong>, our ${esc(d.service)} is live. From then on, for sensitive requests, we'll briefly ask you to confirm your identity — exactly as we explained earlier.`,
    )) +
    `<div style="background:${soft};border-radius:10px;padding:18px 22px;margin:4px 0 18px;">` +
      `<p style="margin:0 0 6px;font-family:${state.font};font-size:14px;font-weight:bold;color:#0E1626;">What do you need to do?</p>` +
      `<p style="margin:0;font-family:${state.font};font-size:14px;line-height:1.6;color:#3A4358;">${T('Nothing in advance. When it is needed, you will automatically receive an SMS with instructions. Keep your smartphone within reach.', "Nothing in advance. When it's needed, you'll automatically receive an SMS with instructions. Keep your smartphone within reach.")}</p>` +
    `</div>` +
    p(state, `<strong>Questions or need help?</strong><br>Our team is ready to assist. ${d.phone ? `Call us on ${esc(d.phone)} or email ` : 'Email '}<a href="mailto:${esc(d.email)}" style="color:${d.primary};">${esc(d.email)}</a>.`) +
    btn(state, d, 'Contact us') +
    p(state, T('Thank you for your trust,<br>The team at ', 'Thanks for your trust,<br>The team at ') + esc(d.company));
  return { subject, html: shell(state, d, inner, `Our identity check goes live on ${d.golive}.`, 'en') };
}

// ── public API ──────────────────────────────────────────────────────────────
export function buildMails(state: MailState, lang: Lang): BuiltMails {
  if (lang === 'en') {
    return {
      announce: buildEnAnnounce(state),
      how:      buildEnHow(state),
      golive:   buildEnGolive(state),
    };
  }
  return {
    announce: buildNlAnnounce(state),
    how:      buildNlHow(state),
    golive:   buildNlGolive(state),
  };
}
